import React, { useState, useRef, useEffect } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  getChainConfig,
  COTI_TESTNET_CHAIN_ID,
  COTI_MAINNET_CHAIN_ID,
  SEPOLIA_CHAIN_ID,
  AVALANCHE_FUJI_CHAIN_ID,
  AVALANCHE_C_CHAIN_ID,
  ETHEREUM_MAINNET_CHAIN_ID,
} from "@/chains";
import { isMainnetPortalBuild, COTI_MAINNET_HEX } from "@/lib/networkMode";

export const COTI_TESTNET_HEX = getChainConfig(COTI_TESTNET_CHAIN_ID)!.hexId;
export { COTI_MAINNET_HEX };
export const SEPOLIA_HEX = getChainConfig(SEPOLIA_CHAIN_ID)!.hexId;
export const AVALANCHE_FUJI_HEX = getChainConfig(AVALANCHE_FUJI_CHAIN_ID)!.hexId;
export const ETHEREUM_HEX = getChainConfig(ETHEREUM_MAINNET_CHAIN_ID)!.hexId;
export const AVALANCHE_HEX = getChainConfig(AVALANCHE_C_CHAIN_ID)!.hexId;

export interface ChainOption {
  id: string;
  name: string;
  icon: string;
  accentColor: string;
  disabled?: boolean;
  /** Scale factor for logos that read small at default size (e.g. ETH / COTI). */
  iconScale?: number;
}

const TESTNET_CHAINS: ChainOption[] = [
  {
    id: SEPOLIA_HEX,
    name: "Sepolia",
    icon: "/icons/eth.svg",
    accentColor: "#627EEA",
    iconScale: 1.2,
  },
  {
    id: AVALANCHE_FUJI_HEX,
    name: "Fuji",
    icon: "/icons/avalanche.svg",
    accentColor: "#E84142",
  },
  {
    id: COTI_TESTNET_HEX,
    name: "COTI Testnet",
    icon: "/icons/coti.svg",
    accentColor: "#4FD1ED",
    iconScale: 1.2,
  },
];

/** Staging / mainnet portal: Avalanche C-Chain + COTI Mainnet only (no Ethereum). */
const MAINNET_CHAINS: ChainOption[] = [
  {
    id: AVALANCHE_HEX,
    name: "Avalanche",
    icon: "/icons/avalanche.svg",
    accentColor: "#E84142",
  },
  {
    id: getChainConfig(COTI_MAINNET_CHAIN_ID)!.hexId,
    name: "COTI",
    icon: "/icons/coti.svg",
    accentColor: "#4FD1ED",
    iconScale: 1.2,
  },
];

/** Networks shown in the selector — mainnet build when staging sets COTI Mainnet default. */
export const DEFAULT_CHAINS: ChainOption[] = isMainnetPortalBuild()
  ? MAINNET_CHAINS
  : TESTNET_CHAINS;

interface NetworkSelectorProps {
  onSelectChain: (chainHexId: string) => void;
  chains?: ChainOption[];
  activeChainId?: string;
  showLabel?: boolean;
  menuAlign?: "left" | "right";
  /** `dropdown` (navbar) or `buttons` (inline segment control above wallet). */
  variant?: "dropdown" | "buttons";
  /** Welcome screen: single pill that connects (no dropdown). */
  connectMode?: boolean;
  connectLabel?: string;
  onConnect?: () => void;
}

const NetworkSelector: React.FC<NetworkSelectorProps> = ({
  onSelectChain,
  chains = DEFAULT_CHAINS,
  activeChainId,
  showLabel = true,
  menuAlign = "left",
  variant = "dropdown",
  connectMode = false,
  connectLabel = "Connect",
  onConnect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const normalizedActiveId = activeChainId?.toLowerCase();
  const activeChain =
    chains.find((chain) => chain.id.toLowerCase() === normalizedActiveId) ??
    chains[0] ??
    DEFAULT_CHAINS[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  const handleSelect = (chainId: string) => {
    onSelectChain(chainId);
    setIsOpen(false);
  };

  const cotiShortIds = new Set([
    COTI_TESTNET_HEX.toLowerCase(),
    COTI_MAINNET_HEX.toLowerCase(),
  ]);

  if (connectMode) {
    return (
      <div className="flex items-center gap-2 md:gap-3">
        {showLabel && (
          <span className="text-muted-foreground text-[10px] md:text-xs uppercase tracking-widest font-medium shrink-0">
            Network
          </span>
        )}
        <button
          type="button"
          onClick={onConnect}
          className="btn-gradient-connect flex h-10 items-center justify-center gap-2 px-4 md:px-5 text-xs md:text-sm font-semibold rounded-full text-white whitespace-nowrap transition-all duration-200 hover:opacity-95"
          style={{ boxShadow: "0 4px 14px rgba(30,41,246,0.3)" }}
          aria-label={`${connectLabel} on ${activeChain.name}`}
        >
          <img
            src={activeChain.icon}
            alt=""
            className="w-4 h-4 md:w-5 md:h-5 shrink-0 rounded-full object-contain"
            style={
              activeChain.iconScale
                ? { transform: `scale(${activeChain.iconScale})` }
                : undefined
            }
          />
          <span>{connectLabel}</span>
        </button>
      </div>
    );
  }

  if (variant === "buttons") {
    return (
      <div
        role="radiogroup"
        aria-label="Network"
        className="inline-flex max-w-full items-center gap-0.5 rounded-full bg-foreground/[0.04] p-1 ring-1 ring-border/80 shadow-sm backdrop-blur-sm dark:bg-white/[0.06] sm:gap-1.5 sm:p-1.5 md:gap-2.5 md:p-2"
      >
        {chains.map((chain) => {
          const isActive = normalizedActiveId === chain.id.toLowerCase();
          const shortName = cotiShortIds.has(chain.id.toLowerCase())
            ? "COTI"
            : chain.name;
          return (
            <button
              key={chain.id}
              type="button"
              role="radio"
              aria-checked={isActive}
              aria-label={chain.name}
              disabled={chain.disabled}
              onClick={() => onSelectChain(chain.id)}
              className={`
                relative flex h-8 items-center gap-1 rounded-full px-2 text-[11px] font-semibold
                transition-all duration-200 whitespace-nowrap
                sm:h-9 sm:gap-1.5 sm:px-3 sm:text-xs
                md:h-11 md:gap-2 md:px-5 md:text-sm
                ${isActive
                  ? "bg-background text-foreground shadow-md ring-1 ring-border dark:bg-white/15"
                  : "text-muted-foreground hover:text-foreground hover:bg-foreground/5"
                }
                ${chain.disabled ? "cursor-not-allowed opacity-50" : ""}
              `}
            >
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full sm:h-6 sm:w-6 md:h-8 md:w-8"
                style={{ background: isActive ? `${chain.accentColor}22` : `${chain.accentColor}12` }}
              >
                <img
                  src={chain.icon}
                  alt=""
                  className="h-3.5 w-3.5 rounded-full object-contain sm:h-4 sm:w-4 md:h-6 md:w-6"
                  style={
                    chain.iconScale
                      ? { transform: `scale(${chain.iconScale})` }
                      : undefined
                  }
                />
              </span>
              <span className="md:hidden">{shortName}</span>
              <span className="hidden md:inline">{chain.name}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 md:gap-3">
      {showLabel && (
        <span className="text-muted-foreground text-[10px] md:text-xs uppercase tracking-widest font-medium shrink-0">
          Network
        </span>
      )}

      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen((o) => !o)}
          className="flex h-10 items-center gap-2 rounded-full bg-foreground/5 px-3 text-xs md:text-sm font-medium text-foreground ring-1 ring-border shadow-sm transition-all duration-200 hover:bg-foreground/10 dark:bg-white/10 dark:hover:bg-white/15"
          style={{ boxShadow: `0 0 0 1px ${activeChain.accentColor}22, 0 4px 14px ${activeChain.accentColor}18` }}
          aria-label={`Network: ${activeChain.name}`}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span
            className="relative flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
            style={{ background: `${activeChain.accentColor}22` }}
          >
            <img
              src={activeChain.icon}
              alt=""
              className="h-3.5 w-3.5 rounded-full object-contain"
              style={
                activeChain.iconScale
                  ? { transform: `scale(${activeChain.iconScale})` }
                  : undefined
              }
            />
          </span>
          <span className="max-w-[7.5rem] truncate md:max-w-none">{activeChain.name}</span>
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div
            role="listbox"
            aria-label="Select network"
            className={`absolute top-full z-50 mt-2 w-[min(16rem,calc(100vw-2rem))] overflow-hidden rounded-2xl dropdown-panel shadow-2xl backdrop-blur-md
                       ${menuAlign === "right" ? "right-0" : "left-0"}`}
          >
            <div className="border-b border-border/60 px-3.5 py-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Switch network
              </p>
            </div>

            <div className="p-1.5">
              {chains.map((chain) => {
                const isActive = normalizedActiveId === chain.id.toLowerCase();
                return (
                  <button
                    key={chain.id}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    disabled={chain.disabled}
                    onClick={() => handleSelect(chain.id)}
                    className={`
                      flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 text-left text-sm
                      transition-colors duration-150
                      ${isActive
                        ? "bg-foreground/10 text-foreground"
                        : "text-muted-foreground hover:bg-muted/80 hover:text-foreground"
                      }
                      ${chain.disabled ? "cursor-not-allowed opacity-50" : ""}
                    `}
                  >
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1 ring-border/70"
                      style={{ background: `${chain.accentColor}18` }}
                    >
                      <img
                        src={chain.icon}
                        alt=""
                        className="h-4 w-4 rounded-full object-contain"
                        style={
                          chain.iconScale
                            ? { transform: `scale(${chain.iconScale})` }
                            : undefined
                        }
                      />
                    </span>
                    <span className="min-w-0 flex-1 font-medium leading-tight text-foreground">
                      {chain.name}
                    </span>
                    {isActive && (
                      <Check className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkSelector;
